import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { labelForActionKind, labelForLogicKind, labelForOutcomeCategory } from './agentFlowCatalog';

const K = { navy: '#121826', blue: '#4185F1', border: 'rgba(18, 24, 38, 0.12)' };

const shell = {
  borderRadius: 1.5,
  border: `1px solid ${K.border}`,
  minWidth: 140,
  px: 1.5,
  py: 1,
};

const layerLabel = {
  trigger: 'Trigger',
  context: 'Contexto',
  logic: 'Lógica',
  action: 'Acción',
  outcome: 'Outcome',
};

function LayerTag({ layer }) {
  if (!layer) return null;
  const t = layerLabel[layer] || layer;
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: 0.02,
        color: alpha(K.navy, 0.45),
        mb: 0.25,
        textTransform: 'uppercase',
      }}
    >
      {t}
    </Typography>
  );
}

export function StartNode({ data }) {
  return (
    <Box sx={{ ...shell, bgcolor: K.navy, color: '#fff', textAlign: 'center' }}>
      <LayerTag layer={data?.layer} />
      <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.75rem' }}>
        {data?.label || 'Inicio'}
      </Typography>
      <Handle type="source" position={Position.Bottom} />
    </Box>
  );
}

function actionPreview(data) {
  const k = data?.actionKind || 'dynamic_message';
  if (k === 'whatsapp_template') return data?.templateName || '…';
  if (k === 'note') return data?.noteBody || '…';
  if (k === 'reminder') return data?.reminderLabel || '…';
  if (k === 'block_gestiones') return data?.blockDurationHours ? `${data.blockDurationHours} h` : '…';
  return data?.message || '…';
}

export function MessageNode({ data }) {
  const kindLabel = labelForActionKind(data?.actionKind);
  return (
    <Box sx={{ ...shell, bgcolor: '#fff', borderColor: alpha(K.blue, 0.35) }}>
      <Handle type="target" position={Position.Top} />
      <LayerTag layer={data?.layer} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontWeight: 700 }}>
        Acción
      </Typography>
      {kindLabel && (
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: alpha(K.navy, 0.55), mb: 0.5 }}>
          {kindLabel}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.35, wordBreak: 'break-word' }}>
        {actionPreview(data)}
      </Typography>
      <Handle type="source" position={Position.Bottom} />
    </Box>
  );
}

export function ConditionNode({ data }) {
  const logicLabel = labelForLogicKind(data?.logicKind);
  return (
    <Box sx={{ ...shell, bgcolor: alpha('#FF9800', 0.08), borderColor: alpha('#FF9800', 0.4) }}>
      <Handle type="target" position={Position.Top} />
      <LayerTag layer={data?.layer} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontWeight: 700 }}>
        Lógica
      </Typography>
      {logicLabel && (
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: alpha(K.navy, 0.55), mb: 0.5 }}>
          {logicLabel}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.35 }}>
        {data?.expression || '…'}
      </Typography>
      <Handle id="yes" type="source" position={Position.Bottom} style={{ left: '35%' }} />
      <Handle id="no" type="source" position={Position.Bottom} style={{ left: '65%' }} />
    </Box>
  );
}

export function EndNode({ data }) {
  const oc = labelForOutcomeCategory(data?.outcomeCategory);
  return (
    <Box sx={{ ...shell, bgcolor: alpha(K.navy, 0.06), textAlign: 'center' }}>
      <Handle type="target" position={Position.Top} />
      <LayerTag layer={data?.layer} />
      {oc && (
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: alpha(K.navy, 0.55), mb: 0.35 }}>
          {oc}
        </Typography>
      )}
      <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.75rem', color: K.navy }}>
        {data?.label || 'Fin'}
      </Typography>
    </Box>
  );
}

export const agentFlowNodeTypes = {
  start: StartNode,
  message: MessageNode,
  condition: ConditionNode,
  end: EndNode,
};
